import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { IconChevronLeft, IconCheck, IconX, IconClock } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import { isMatching } from 'ts-pattern';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { capture } from '@tupai/analytics/client';
import { useMinWidth } from '@tupai/shell';
import { useLinksStore } from '@/store/links-store';
import { useChildLink } from '@/lib/use-child-link';
import { getAssignmentResult, ApiError } from '@/lib/api';
import type { AssignmentResultResponse } from '@/lib/api';
import type { PracticeMcqStep, PracticeQuestionResult, PracticeStepResult } from '@tupai/types';
import { PageSpinner, PageError, PageEmpty } from '@/components/page-state';
import BottomSheet from '@/components/bottom-sheet';
import ChildHubPage from '@/components/child-hub-page';
import { fmtDayMonth, fmtTime } from '@/lib/format';
import { useLanguage, type AppLocale } from '@/lib/language';
import { useTranslation, type TFunction } from '@tupai/i18n/react';
import { isSampleId } from '@/lib/sample-child';
import { apiErrorMessage } from '@/lib/api-errors';

export const Route = createFileRoute('/_protected/children/$linkId/homework/$assignmentId')({
  component: HomeworkResultPage,
});

function resultFor(
  results: PracticeQuestionResult[],
  index: number
): PracticeQuestionResult | undefined {
  return results.find(isMatching({ question_index: index }));
}

interface AnswerLine {
  label: string | null; // "(a)" for multi-step, null for a single step
  text: string;
}

interface RowVM {
  index: number;
  correct: boolean;
  degraded: boolean;
  questionText?: string;
  answers?: AnswerLine[];
}

// Same rule as the student app's stripOptionLabel (practice.tsx:180) — mcq
// options embed "A. "/"B) " prefixes that mean nothing outside the picker.
function stripOptionLabel(opt: string): string {
  return opt.replace(/^[A-Da-d]\s*[.):]\s*/, '');
}

function stepAnswerLine(
  step: PracticeMcqStep,
  sr: PracticeStepResult | undefined,
  wrong: boolean,
  t: TFunction<'hub'>
): string {
  const rawAnswered =
    step.input_type === 'mcq' ? step.options?.[sr?.selected_index ?? -1] : sr?.text_answer;
  const answered = rawAnswered != null ? stripOptionLabel(rawAnswered) : undefined;
  let text = t('answered', { answer: answered ?? '—' });
  if (wrong) {
    const rawCorrect =
      step.input_type === 'mcq'
        ? step.options?.[step.correct_index ?? -1]
        : step.accepted_answers?.[0];
    if (rawCorrect) text += ` · ${t('correctAnswerIs', { answer: stripOptionLabel(rawCorrect) })}`;
  }
  return text;
}

// QA PHWK-042: question_set/results are opaque stored JSON, never re-graded
// or shape-validated server-side. One throw anywhere in here must fall back
// to the score card alone, never a blank page — so every row is built inside
// this single try/catch rather than trusting the shape field by field.
function buildRows(
  questions: unknown[],
  results: PracticeQuestionResult[],
  t: TFunction<'hub'>
): RowVM[] | null {
  try {
    return questions.map((raw, index) => {
      const r = resultFor(results, index);
      const correct = r?.all_correct === true;
      const q = raw as { pending?: boolean; question_text?: string; steps?: unknown } | null;
      const steps = q?.steps;

      if (
        !q ||
        typeof q !== 'object' ||
        q.pending === true ||
        !Array.isArray(steps) ||
        steps.length === 0
      ) {
        return { index, correct, degraded: true };
      }

      const multiStep = steps.length > 1;
      const answers: AnswerLine[] = steps.map((step: PracticeMcqStep, si: number) => {
        const sr = r?.steps?.find((s) => s && s.step_index === step.step_index);
        const wrong = multiStep ? sr?.correct !== true : !correct;
        return {
          label: multiStep ? `(${String.fromCharCode(97 + si)})` : null,
          text: stepAnswerLine(step, sr, wrong, t),
        };
      });

      return { index, correct, degraded: false, questionText: q.question_text, answers };
    });
  } catch {
    return null;
  }
}

// Question text always carries math (AssignmentWorksheetPrintable's recipe);
// answer/meta text usually doesn't, but may (a fraction, a root) — simplest
// correct approach is running both through the same recipe unconditionally
// rather than sniffing for '$' first.
function MathLine({ text, className }: { text: string; className: string }) {
  return (
    <div className={`${className} math-content`}>
      <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

function subLine(data: AssignmentResultResponse, t: TFunction<'hub'>, locale: AppLocale): string {
  const base = t('subLineBase', { studentName: data.student_name });
  if (data.completed_at) {
    const done = t('subLineDone', {
      date: fmtDayMonth(data.completed_at, locale),
      time: fmtTime(data.completed_at, locale),
    });
    return `${base} · ${done}`;
  }
  if (data.started_at) {
    return `${base} · ${t('subLineStarted', { date: fmtDayMonth(data.started_at, locale) })}`;
  }
  return base;
}

interface ResultBodyProps {
  loading: boolean;
  notStarted: boolean;
  error: string | null;
  data: AssignmentResultResponse | null;
  questions: unknown[] | null;
  results: PracticeQuestionResult[];
  rows: RowVM[] | null;
  minutes: number | null;
  onRetry: () => void;
  /** Padding for the loaded body only. The loading/empty/error states are
   *  full-region treatments that centre themselves on `flex-1` and carry
   *  their own inset — they must stay flush, which is why the caller's
   *  scroll wrapper is a bare flex column and the gutter lives here. */
  bodyClass: string;
}

// The score card + question rows — shared by the phone page's own scrolling
// body and the `lg`+ dialog's body (WP2), so the two states never drift.
function ResultBody({
  loading,
  notStarted,
  error,
  data,
  questions,
  results,
  rows,
  minutes,
  onRetry,
  bodyClass,
}: ResultBodyProps) {
  const { t } = useTranslation('hub');
  if (loading) return <PageSpinner />;
  if (notStarted) {
    return (
      <PageEmpty
        icon={<IconClock size={26} stroke={2} />}
        title={t('notStarted')}
        body={t('notStartedBody')}
      />
    );
  }
  if (error || !data) return <PageError message={error ?? undefined} onRetry={onRetry} />;

  return (
    <div className={`flex flex-col gap-3 ${bodyClass}`}>
      <div className="rounded-lg border border-border p-4 text-center">
        <p className="font-display text-3xl text-green">
          {data.correct_count}/{data.total_questions}
        </p>
        <p className="text-sm text-muted font-semibold mt-1">
          {t('correctSuffix')}
          {minutes != null ? ` · ${t('finishedIn', { minutes })}` : ''}
        </p>
        {questions && questions.length > 0 && (
          <div className="flex gap-1 mt-3">
            {questions.map((_, i) => (
              <span
                key={i}
                className={`flex-1 h-1.5 rounded-pill ${
                  resultFor(results, i)?.all_correct === true ? 'bg-green' : 'bg-error'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {rows && rows.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          {rows.map((row) => (
            <div
              key={row.index}
              className={`flex gap-2.5 px-3.5 py-3 border-b border-border-light last:border-b-0 ${
                !row.correct ? 'bg-error/5' : ''
              }`}
            >
              <span className="shrink-0 mt-0.5">
                {row.correct ? (
                  <IconCheck size={16} stroke={2.5} className="text-green" />
                ) : (
                  <IconX size={16} stroke={2.5} className="text-error" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                {row.degraded ? (
                  <p className="text-sm text-hint font-semibold">
                    {row.index + 1} · {t('detailUnavailable')}
                  </p>
                ) : (
                  <>
                    <MathLine
                      text={`${row.index + 1} · ${row.questionText ?? ''}`}
                      className="text-sm font-semibold text-content"
                    />
                    {row.answers?.map((a, ai) => (
                      <MathLine
                        key={ai}
                        text={a.label ? `${a.label} ${a.text}` : a.text}
                        className="text-sm text-muted font-medium mt-0.5"
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          ))}
          <p className="text-center text-hint text-sm py-2.5">
            {t('allQuestionsListed', { total: data.total_questions })}
          </p>
        </div>
      )}
    </div>
  );
}

// /children/:linkId/homework/:assignmentId — read-only result view for one
// completed (or deep-linked, still-in-progress) assignment. The endpoint
// 200s the moment practice_id is set, so completed_at may be null here —
// every branch below tolerates that (WP5).
function HomeworkResultPage() {
  const { linkId, assignmentId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { parentLinks } = useLinksStore();
  const locale = useLanguage();
  const { t } = useTranslation('hub');
  const { t: tc } = useTranslation('common');

  const [data, setData] = useState<AssignmentResultResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notStarted, setNotStarted] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const fetchId = useRef(0);
  // From `lg`, this route is a dialog over the child hub (canvas
  // A-Homework), same recipe as ChildReportViewPage's `isDesktop` branch —
  // behaviour switch only, layout stays CSS everywhere else.
  const isDesktop = useMinWidth('lg');

  const link = useChildLink(linkId);
  const linkMissing = !link && parentLinks.length > 0;
  useEffect(() => {
    if (linkMissing) void navigate({ to: '/children', replace: true });
  }, [linkMissing, navigate]);

  useEffect(() => {
    if (!linkId || !assignmentId) return;
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);
    setNotStarted(false);
    getAssignmentResult(assignmentId)
      .then((res) => {
        if (fetchId.current !== id) return;
        setData(res);
      })
      .catch((err) => {
        if (fetchId.current !== id) return;
        if (err instanceof ApiError && (err.status === 409 || err.code === 'not_started')) {
          setNotStarted(true);
        } else {
          setError(apiErrorMessage(err, tc));
        }
      })
      .finally(() => {
        if (fetchId.current !== id) return;
        setLoading(false);
      });
  }, [linkId, assignmentId, retryTick]);

  // `homework_result_viewed` — fires once the result payload has actually
  // loaded (the "not started" / error branches show no result, so they don't
  // count as a view of one), keyed on assignmentId.
  const resultViewedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data || !assignmentId || isSampleId(assignmentId)) return;
    if (resultViewedForRef.current === assignmentId) return;
    resultViewedForRef.current = assignmentId;
    capture('homework_result_viewed', { assignment_id: assignmentId, link_id: linkId });
  }, [data, assignmentId, linkId]);

  if (!link) {
    return parentLinks.length === 0 ? <PageSpinner /> : null;
  }

  const questions = Array.isArray(data?.question_set) ? data.question_set : null;
  const results = Array.isArray(data?.results) ? (data.results as PracticeQuestionResult[]) : [];
  const rows = data && questions ? buildRows(questions, results, t) : null;

  const minutes =
    data?.completed_at && data?.started_at
      ? Math.max(
          1,
          Math.round(
            (new Date(data.completed_at).getTime() - new Date(data.started_at).getTime()) / 60000
          )
        )
      : null;

  const title = data?.chapter_title ?? t('homeworkFallbackTitle');
  // Dialog branch replaces so Back doesn't reopen the dialog (the hub
  // underneath is already the right history entry) — same idiom as
  // ChildReportViewPage's backToHubFromDialog.
  const backToHubFromDialog = () =>
    navigate({ to: '/children/$linkId', params: { linkId }, replace: true });

  if (isDesktop) {
    // Homework result is a dialog over the child hub from `lg` (canvas
    // A-Homework) — the hub renders as the backdrop, the result lives in a
    // `bare` sheet that owns its own header (title + sub line + X) and body.
    return (
      <>
        <ChildHubPage backdrop />
        <BottomSheet open wide bare onClose={backToHubFromDialog} ariaLabel={title}>
          <div className="flex h-14 items-center gap-2 border-b border-border px-4">
            <p className="flex-1 truncate text-base font-semibold">{title}</p>
            {data && (
              <p className="text-sm text-muted font-semibold whitespace-nowrap">
                {subLine(data, t, locale)}
              </p>
            )}
            <button
              aria-label={tc('close')}
              onClick={backToHubFromDialog}
              className="flex size-11 items-center justify-center rounded-pill text-muted hover:bg-bg cursor-pointer"
            >
              <IconX size={20} stroke={2} />
            </button>
          </div>

          <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
            <ResultBody
              loading={loading}
              notStarted={notStarted}
              error={error}
              data={data}
              questions={questions}
              results={results}
              rows={rows}
              minutes={minutes}
              onRetry={() => setRetryTick((t) => t + 1)}
              bodyClass="px-5 py-5"
            />
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface">
      {/* Page-head owned by this page (prototype figure 1), rendered in
          every state — the back button always works, even mid-fetch. Back
          is user-locked to the hub, never "go back" browser history. */}
      <div className="bg-surface border-b border-border-light pl-1 pr-4 py-2 flex-shrink-0 safe-area-top flex items-center gap-1">
        <button
          aria-label={t('back')}
          onClick={() => navigate({ to: '/children/$linkId', params: { linkId } })}
          className="w-11 h-11 rounded-pill flex items-center justify-center text-content transition-all hover:bg-bg cursor-pointer shrink-0"
        >
          <IconChevronLeft size={20} stroke={2.5} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-lg font-bold text-content truncate">{title}</p>
          {data && (
            <p className="text-sm text-muted font-semibold truncate">{subLine(data, t, locale)}</p>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
        <ResultBody
          loading={loading}
          notStarted={notStarted}
          error={error}
          data={data}
          questions={questions}
          results={results}
          rows={rows}
          minutes={minutes}
          onRetry={() => setRetryTick((t) => t + 1)}
          bodyClass="px-4 py-4"
        />
      </div>
    </div>
  );
}
